import { z } from 'zod';
import { Locale, LocalDate, LocalTime, MinorAmount, TenantSlug, TimeZone, Uuid } from './common.js';
// Публичная витрина салона: то, что видит человек, ещё не являющийся клиентом,
// и то, что отдаётся в бот, виджет и мобильное приложение.
//
// ГЛАВНОЕ ПРАВИЛО ФАЙЛА: здесь описан ровно публичный срез, а не «модель минус пара полей».
// Наружу не выходят внутренние заметки, себестоимость и наценки, персональные телефоны
// и учётные записи сотрудников, флаги биллинга, буферы и время уборки, а также сами
// признаки активности — витрина показывает только то, что активно, и отсутствие услуги
// в выдаче не повод сообщать, что она вообще существует. Любое новое поле в этих схемах —
// это осознанная публикация данных салона, а не техническая деталь сериализации.
// Что запрашивает витрина. Салон здесь только по публичному slug: внутренние
// идентификаторы тенанта в публичный контур не попадают вовсе.
export const PublicCatalogQuery = z.object({
    tenantSlug: TenantSlug,
    // Сузить выдачу до одной точки. Без него отдаются все активные филиалы.
    branchId: Uuid.optional(),
    // Язык витрины; по умолчанию язык салона.
    locale: Locale.optional(),
});
/**
 * Регулярные часы работы филиала. Настенное время, а не моменты: несколько строк
 * на один день недели — это легальный перерыв на обед (9:00–13:00 и 14:00–20:00),
 * а не противоречие.
 */
export const PublicBranchHours = z.object({
    // День недели, 0 = воскресенье — как EXTRACT(DOW) в PostgreSQL; своей нумерации не заводим.
    weekday: z.number().int().min(0).max(6),
    opensAt: LocalTime,
    closesAt: LocalTime,
});
/**
 * Исключение из графика на конкретную дату. Клиенту его показывают текстом
 * («31 декабря работаем до 16:00»), поэтому reason — публичное поле,
 * и салон обязан понимать, что пишет туда для посторонних глаз.
 */
export const PublicBranchHoursException = z.object({
    onDate: LocalDate,
    isClosed: z.boolean(),
    opensAt: LocalTime.nullable(),
    closesAt: LocalTime.nullable(),
    reason: z.string().nullable(),
});
/**
 * Филиал на витрине.
 *
 * timezone отдаётся всегда: без неё клиент прочитает время своим часовым поясом.
 * Телефон здесь — опубликованный номер точки, а не личный номер сотрудника;
 * координаты нужны карте филиалов и микроразметке LocalBusiness.
 */
export const PublicBranch = z.object({
    id: Uuid,
    name: z.string(),
    timezone: TimeZone,
    address: z.string().nullable(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    // Публичный телефон точки (E.164). Телефонов сотрудников в публичной выдаче нет.
    phoneE164: z.string().nullable(),
    // Параметры сетки записи: клиенту нужно понимать, почему ближайшие часы
    // недоступны и как далеко вперёд вообще можно записаться.
    slotStepMinutes: z.number().int().positive(),
    minLeadMinutes: z.number().int().nonnegative(),
    maxHorizonDays: z.number().int().positive(),
    hours: z.array(PublicBranchHours),
    hoursExceptions: z.array(PublicBranchHoursException),
});
// Категория каталога. Дерево через parentId: каталог на 200 услуг плоским списком нечитаем.
export const PublicServiceCategory = z.object({
    id: Uuid,
    parentId: Uuid.nullable(),
    name: z.string(),
    description: z.string().nullable(),
    ord: z.number().int(),
});
/**
 * Услуга на витрине.
 *
 * durationMinutes — работа мастера, которую видит клиент: буферы до и после услуги
 * и время уборки ресурса сюда НЕ входят. Это внутренняя кухня салона, по ней
 * считается занятость, но клиенту она показывает «стрижка 50 минут» вместо 30
 * и выглядит как обман. Себестоимости и наценок в публичном контуре нет вовсе.
 */
export const PublicService = z.object({
    id: Uuid,
    categoryId: Uuid.nullable(),
    name: z.string(),
    description: z.string().nullable(),
    durationMinutes: z.number().int().positive(),
    // Базовая цена. Итоговую цену конкретного слота (с учётом мастера и правил цены)
    // отдаёт только поиск слотов: здесь она была бы обещанием, которое салон не давал.
    priceMinor: MinorAmount,
    // Групповое занятие («йога, 12 мест») клиент должен отличать от личного визита.
    isGroup: z.boolean(),
    // Мест в группе; для личной услуги всегда 1.
    capacity: z.number().int().positive(),
    ord: z.number().int(),
});
/**
 * Мастер на витрине.
 *
 * Имя — публичное («Аня»), а не то, что стоит в кадрах. Сюда не попадают
 * учётная запись, телефон, дата найма, цвет колонки в журнале и флаги тарификации:
 * это данные салона о сотруднике, а не карточка для клиента.
 */
export const PublicEmployee = z.object({
    id: Uuid,
    publicName: z.string(),
    positionName: z.string().nullable(),
    avatarUrl: z.url().nullable(),
    about: z.string().nullable(),
    // Что этот мастер оказывает — витрина фильтрует список услуг по выбранному мастеру
    // и наоборот. Персональные цены и длительности мастера здесь не раскрываются:
    // они видны в цене конкретного слота.
    serviceIds: z.array(Uuid),
    // Филиалы, где мастера можно застать.
    branchIds: z.array(Uuid),
});
// Шапка витрины: как называется салон и на каком языке он говорит с клиентом.
export const PublicTenant = z.object({
    slug: TenantSlug,
    name: z.string(),
    defaultLocale: Locale,
});
/**
 * Витрина целиком — один ответ на первый экран.
 *
 * Плоские списки со ссылками по id, а не вложенное дерево: те же услуги
 * нужны и экрану мастера, и экрану категории, и дублировать их в двух ветках ответа —
 * это разъезжающиеся копии одной и той же цены.
 */
export const PublicCatalog = z.object({
    tenant: PublicTenant,
    branches: z.array(PublicBranch).min(1),
    categories: z.array(PublicServiceCategory),
    services: z.array(PublicService),
    employees: z.array(PublicEmployee),
});
//# sourceMappingURL=catalog.js.map